import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/common/PageHeader';
import { BatchMonitoringTab } from '../../components/data-integration/BatchMonitoringTab';
import { DataIntegrationHelpRail } from '../../components/data-integration/DataIntegrationHelpRail';
import { ApiReferencePanel } from '../../components/api-reference/ApiReferencePanel';
import { ResourceCountsTab } from '../../components/data-integration/ResourceCountsTab';
import { SchemaDefinitionsWithMappings } from '../../components/SchemaDefinitionsWithMappings';
import { SetupGuidesMenu } from '../../components/schema-editor/SetupGuidesMenu';
import { SenderProfilesTab } from '../data-integration/sender-profiles';
import { useAllFlags } from '../../hooks/useAllFlags';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import styles from './DataIntegrationPage.module.css';

const DATA_INTEGRATION_INTRO =
  "Define a schema for each kind of record you have — customers, transactions, locations, or anything custom — then map its fields to Reach's core concepts. Reach uses your data to power segments, messaging, and attribution.";

type TabName = 'schemas' | 'batches' | 'counts' | 'api-reference' | 'sender-profiles';

const VALID_TABS: TabName[] = ['schemas', 'batches', 'counts', 'api-reference', 'sender-profiles'];

/**
 * Data Integration Settings Page
 * Combined interface for schema definitions, field mappings, and batch monitoring
 */
export const DataIntegrationPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { ld } = useAllFlags();
  const showSenderProfiles = ld['enable-sender-profiles'] ?? false;

  // Initialize activeTab from URL parameter, default to 'schemas'
  const [activeTab, setActiveTab] = useState<TabName>(() => {
    const initialTab = tab && VALID_TABS.includes(tab as TabName) ? (tab as TabName) : 'schemas';
    return initialTab;
  });

  // Handle URL changes (browser back/forward) - sync activeTab with URL
  useEffect(() => {
    const urlTab = tab && VALID_TABS.includes(tab as TabName) ? (tab as TabName) : 'schemas';

    setActiveTab(urlTab);
    // Mark that initial mount is complete
    isInitialMount.current = false;
  }, [tab]);

  // Helper function to handle tab navigation
  const handleTabChange = (newTab: string) => {
    if (!VALID_TABS.includes(newTab as TabName)) {
      return;
    }

    const tabName = newTab as TabName;

    // Don't navigate if we're already on the correct tab or on initial mount
    if (tabName === activeTab || isInitialMount.current) {
      return;
    }

    const currentPath = '/dashboard/settings/data-integration';
    const newPath = tabName === 'schemas' ? currentPath : `${currentPath}/${tabName}`;

    // Update both state and URL
    setActiveTab(tabName);
    navigate(newPath, { replace: true });
  };

  const showsHelpRail = activeTab !== 'schemas' && activeTab !== 'api-reference';

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <div className={styles.header}>
          <PageHeader title="Data Integration" />
          <p className={styles.intro}>{DATA_INTEGRATION_INTRO}</p>
        </div>
        <Tabs value={activeTab} onValueChange={handleTabChange} defaultValue={activeTab}>
          <div className={styles.tabsRow}>
            <TabsList>
              <TabsTrigger value="schemas">Schema Definitions & Mappings</TabsTrigger>
              {showSenderProfiles && <TabsTrigger value="sender-profiles">Sender Profiles</TabsTrigger>}
              <TabsTrigger value="batches">Batch Uploads</TabsTrigger>
              <TabsTrigger value="counts">Resource Counts</TabsTrigger>
              <TabsTrigger value="api-reference">API Reference</TabsTrigger>
            </TabsList>
            {activeTab === 'schemas' && <SetupGuidesMenu />}
          </div>
          <TabsContent value="schemas" className="mt-6">
            <SchemaDefinitionsWithMappings />
          </TabsContent>
          {showSenderProfiles && (
            <TabsContent value="sender-profiles" className="mt-6">
              <SenderProfilesTab />
            </TabsContent>
          )}
          <TabsContent value="batches" className="mt-6">
            <BatchMonitoringTab />
          </TabsContent>
          <TabsContent value="counts" className="mt-6">
            <ResourceCountsTab />
          </TabsContent>
          <TabsContent value="api-reference" className="mt-6">
            <ApiReferencePanel />
          </TabsContent>
        </Tabs>
      </div>
      {/* The Schemas tab carries its own Setup guides menu and attribute
          panel, and the API reference needs the full width. */}
      {showsHelpRail && <DataIntegrationHelpRail />}
    </div>
  );
};
